import { Alert, Button, Card, Col, Divider, Form, Image, Input, Layout, Modal, Radio, Result, Row, Select, Space, Spin, Table, Typography } from 'antd';
import axios from 'axios';
import { ethers } from 'ethers';
import moment from 'moment';
import qs from "qs";
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import OriginLeaderboardContractJson from "../../contracts/OriginLeaderboard.json";
import OriginPrisonerContractJson from "../../contracts/OriginPrisoner.json";
import { Community } from "../components/Community";
import { NicknameChanger } from "../components/dashboard/NicknameChanger";
import { Footer } from "../components/Footer";
import { Navigation } from "../components/Navigation";
import { GAME_VERSION, NETWORK_CHAIN_ID, NETWORK_NAME, ORIGIN_LEADERBOARD_CONTRACT_ADDRESS, ORIGIN_PRISONER_CONTRACT_ADDRESS } from "../Constants";
import * as analytics from "../services/Analytics";
import { getNickname } from "../services/Nicknames";
import { IS_VOTING_SPACE_ENABLED } from "../Constants";

export function Dashboard() {

    /** @type {import('@ethersproject/providers').ExternalProvider} */
    const ethereum = window.ethereum;

    const [isLoading, setIsLoading] = useState(true);

    const [isConnectedAccountTokensLoading, setIsConnectedAccountTokensLoading] = useState(false);

    const [isMintDataLoading, setIsMintDataLoading] = useState(false);

    const [isLeaderboardLoading, setIsLeaderboardLoading] = useState(false);

    const [isCreativeSpaceLoading, setIsCreativeSpaceLoading] = useState(true);

    const [isVotingSpaceLoading, setIsVotingSpaceLoading] = useState(false);

    const [isNetworkChainIdCorrect, setIsNetworkChainIdCorrect] = useState(false);

    /** @type {string} */
    const [connectedAccount, setConnectedAccount] = useState(null);

    /** @type {[Array, Function]} */
    const [connectedAccontTokens, setConnectedAccountTokens] = useState(null);

    const [mintData, setMintData] = useState(null);

    /** @type {[Array, Function]} */
    const [leaderboardRecords, setLeaderboardRecords] = useState(null);

    /** @type {[import('ethers').Contract, Function]} */
    const [originPrisonerContract, setOriginPrisonerContract] = useState(null);

    /** @type {[import('ethers').Contract, Function]} */
    const [originLeaderboardContract, setOriginLeaderboardContract] = useState(null);

    const [submitIdeaForm] = Form.useForm();

    const [submitDecisionForm] = Form.useForm();

    async function getConnectedAccount() {
        try {

            setIsLoading(true);

            // Break checkig if provider is not exists
            if (!ethereum) {
                return;
            }

            // Check if user has authorized accounts
            const accounts = await ethereum.request({ method: 'eth_accounts' });
            if (accounts.length === 0) {
                return;
            }

            // Get first authorized account
            const account = accounts[0];
            console.log('Found an authorized account:', account);
            setConnectedAccount(account);

            analytics.connectAccount(account);

            await checkNetwork();

        } catch (error) {
            console.log(error);
            analytics.getError("Failed to get connected account: " + JSON.stringify(error));
        } finally {
            setIsLoading(false);
        }
    }

    async function connectAccount() {
        try {

            setIsLoading(true);

            analytics.clickConnectAccount();

            // If provider is not exists
            if (!ethereum) {
                Modal.info({
                    title: "MetaMask not found",
                    content: "You need to setup MetaMask wallet to continue",
                    okText: "Download MetaMask",
                    onOk() { window.open("https://metamask.io") },
                });
                return;
            }

            // Request access to account
            const accounts = await ethereum.request({
                method: 'eth_requestAccounts',
            });

            console.log('Connected account', accounts[0]);
            setConnectedAccount(accounts[0]);

            analytics.connectAccount(accounts[0]);

            await checkNetwork();

        } catch (error) {
            console.log(error);
            analytics.getError("Failed to connect account: " + JSON.stringify(error));
        } finally {
            setIsLoading(false);
        }
    }

    async function checkNetwork() {
        try {

            if (!ethereum) {
                return;
            }

            const provider = new ethers.providers.Web3Provider(ethereum);
            const providerNetwork = await provider.getNetwork();

            if (providerNetwork.chainId === NETWORK_CHAIN_ID) {
                setIsNetworkChainIdCorrect(true);
            }

        } catch (error) {
            console.log(error);
            analytics.getError("Failed to check network: " + JSON.stringify(error));
        }
    }

    async function loadConnectedAccountTokens() {
        try {

            setIsConnectedAccountTokensLoading(true);

            /** @type {Array.<import('ethers').BigNumber>} */
            const accountTokenIds = await originPrisonerContract.walletOfOwner(connectedAccount);

            var connectedAccountTokens = [];
            for (let i = 0; i < accountTokenIds.length; i++) {
                // Get token URI from contract
                const accountTokenId = accountTokenIds.at(i);
                const accountTokenURI = await originPrisonerContract.tokenURI(accountTokenId);
                // Get token json using received token URI
                const accountTokenURIGetResponse = await axios.get(accountTokenURI);
                const accountTokenJson = await accountTokenURIGetResponse.data;
                // Push to result array
                connectedAccountTokens.push(accountTokenJson);
            }

            setConnectedAccountTokens(connectedAccountTokens);

        } catch (error) {
            console.log(error);
            analytics.getError("Failed to load connected account tokens: " + JSON.stringify(error));
        } finally {
            setIsConnectedAccountTokensLoading(false);
        }
    }

    async function loadMintData() {
        try {

            setIsMintDataLoading(true);

            const maxSupply = await originPrisonerContract.maxSupply();
            const totalSupply = await originPrisonerContract.totalSupply();
            const cost = await originPrisonerContract.cost();
            const mintData = {
                maxSupplyString: maxSupply.toString(),
                totalSupplyString: totalSupply.toString(),
                cost: cost,
                costString: ethers.utils.formatEther(cost),
            };

            setMintData(mintData);

        } catch (error) {
            console.log(error);
            analytics.getError("Failed to load mint data: " + JSON.stringify(error));
        } finally {
            setIsMintDataLoading(false);
        }
    }

    async function loadLeaderboard() {
        try {

            setIsLeaderboardLoading(true);

            const leaderboardRecords = await originLeaderboardContract.getRecords(GAME_VERSION);

            var sortedLeaderboardRecords = [...leaderboardRecords];

            sortedLeaderboardRecords.sort((a, b) => a.score.lt(b.score) ? 1 : -1);

            setLeaderboardRecords(sortedLeaderboardRecords);

        } catch (error) {
            console.log(error);
            analytics.getError("Failed to load leaderboard: " + JSON.stringify(error));
        } finally {
            setIsLeaderboardLoading(false);
        }
    }

    async function onFinishMintTokensForm(values) {
        try {

            setIsMintDataLoading(true);

            analytics.clickMint();

            const amount = values.amount;
            await originPrisonerContract.mint(amount, { value: mintData.cost.mul(amount) });

            Modal.info({
                title: "Congratulations 🎉",
                content: "Your NFTs will soon appear in your collection. Provided that the transaction was successful.",
            });

            analytics.mint();

            await loadMintData();

        } catch (error) {
            console.log(error);
            analytics.getError("Failed to finish mint tokens form: " + JSON.stringify(error));
            Modal.error({
                title: "Ooops...",
                content:
                    <Space direction='vertical'>
                        <Typography.Text>Failed to mint NFTs. Try later.</Typography.Text>
                        {error && error.data && error.data.message &&
                            <Alert
                                style={{ marginTop: "16px" }}
                                type="error"
                                message={<Typography.Text style={{ wordBreak: 'break-word' }}>{error.data.message}</Typography.Text>} />
                        }
                    </Space>,
            });
        } finally {
            setIsMintDataLoading(false);
        }
    }

    async function onFinishSubmitIdeaForm(values) {
        try {

            setIsCreativeSpaceLoading(true);

            analytics.clickSubmitIdea();

            const url = 'https://formsubmit.co/ajax/cryptospaceprison@gmail.com';
            const data = qs.stringify({
                form: "submit_idea",
                account: connectedAccount,
                contact: values.contact,
                idea: values.idea,
            });
            await axios.post(url, data);

            submitIdeaForm.resetFields();

            Modal.info({
                title: "Thanks for your cool idea 🙌"
            });

        } catch (error) {
            console.log(error);
            analytics.getError("Failed to finish submit idea form: " + JSON.stringify(error));
            Modal.error({
                title: "Ooops...",
                content: "Failed to submit your idea. Try later."
            });
        } finally {
            setIsCreativeSpaceLoading(false);
        }
    }

    async function onFinishSubmitDecisionForm(values) {
        try {

            setIsVotingSpaceLoading(true);

            analytics.clickSubmitDecision();

            const url = 'https://formsubmit.co/ajax/cryptospaceprison@gmail.com';
            const data = qs.stringify({
                form: "submit_decision",
                account: connectedAccount,
                decision: values.decision,
            });
            await axios.post(url, data);

            submitDecisionForm.resetFields();

            Modal.info({
                title: "You have made your decision. The result of the voting will be known on March 13."
            });

        } catch (error) {
            console.log(error);
            analytics.getError("Failed to finish submit decision form: " + JSON.stringify(error));
            Modal.error({
                title: "Ooops...",
                content: "Failed to submit your decision. Try later."
            });
        } finally {
            setIsVotingSpaceLoading(false);
        }
    }

    function renderLeaderboard() {

        if (!leaderboardRecords) {
            return null;
        }

        const columns = [
            {
                title: 'Position',
                key: 'position',
                render: (text, record, index) => "#" + (index + 1)
            },
            {
                title: 'Player',
                dataIndex: 'player',
                key: 'player',
                render: (text) => {
                    let result = text.substring(0, 6) + "..." + text.substring(text.length - 4);
                    if (text.toUpperCase() === connectedAccount.toUpperCase()) {
                        result = <Typography.Text strong type='warning'>{result} (You account)</Typography.Text>;
                    }
                    return result;
                }
            },
            {
                title: 'Date',
                dataIndex: 'date',
                key: 'date',
                render: (text) => moment.unix(text.toNumber()).format("LLL")
            },
            {
                title: 'Score',
                dataIndex: 'score',
                key: 'score',
                render: (text) => text.toNumber()
            },
        ];

        return (<Table columns={columns} dataSource={leaderboardRecords} rowKey={record => record.player} pagination={false} scroll={{ x: true }} />)
    }

    function renderContent() {

        if (isLoading) {
            return (
                <div className="layout-content-element"></div>
            );
        }

        // If required to connect account
        else if (!connectedAccount) {
            return (
                <div className="layout-content-element">
                    <Result
                        icon={<div />}
                        title={<Typography.Title level={1}>🚀 Dashboard</Typography.Title>}
                        subTitle="You need to connect MetaMask wallet to continue"
                        extra={<Button size='large' onClick={() => connectAccount()}>Connect Metamask</Button>}
                    />
                </div>
            )
        }

        // If required switch network 
        else if (!isNetworkChainIdCorrect) {
            return (
                <div className="layout-content-element">
                    <Result
                        icon={<div />}
                        title={<Typography.Title level={1}>🚀 Dashboard</Typography.Title>}
                        subTitle={`You are connected to the wrong network. Please switch to ${NETWORK_NAME} and reload page.`}
                        extra={<Button size='large' onClick={() => window.location.reload()}>Reload page</Button>}
                    />
                </div>
            )
        }

        else {

            return (
                <div>
                    {/* Connected account tokens */}
                    <Spin spinning={isConnectedAccountTokensLoading}>
                        <div className="layout-content-element">
                            <Typography.Title level={1}>Your prisoners</Typography.Title>
                            <Alert
                                message={`👤 Connected account: ${connectedAccount.substring(0, 6)}...${connectedAccount.substring(connectedAccount.length - 4)}`}
                                type="info"
                            />
                            <div style={{ margin: "24px 0px" }}>
                                <Button onClick={() => { loadConnectedAccountTokens() }}>Sync prisoners</Button>
                            </div>
                            {/* If account has tokens */}
                            {connectedAccontTokens && connectedAccontTokens.length !== 0 &&
                                <Row gutter={16}>
                                    {connectedAccontTokens.map((token, index) => {
                                        return (
                                            <Col key={index} lg={{ span: 8 }} sm={{ span: 24 }}>
                                                <Card cover={<img alt="NFT" src={token.image} />} style={{ margin: "16px 0px" }}>
                                                    <Space direction='vertical' style={{ width: "100%" }}>
                                                        <div>
                                                            <Typography.Text type='secondary'>Name: </Typography.Text>
                                                            <Typography.Text>{token.name}</Typography.Text>
                                                        </div>
                                                        <div>
                                                            <Typography.Text type='secondary'>Nickname: </Typography.Text>
                                                            <Typography.Text>{getNickname(token.edition)}</Typography.Text>
                                                        </div>
                                                        <Divider />
                                                        <Link to="/game" state={{ token: token }}>
                                                            <Button type="primary" onClick={() => analytics.clickPlay()} >Play</Button>
                                                        </Link>
                                                        <NicknameChanger account={connectedAccount} token={token} />
                                                    </Space>
                                                </Card>
                                            </Col>
                                        )
                                    })}
                                </Row>
                            }
                            {/* If account not has tokens */}
                            {connectedAccontTokens && connectedAccontTokens.length === 0 &&
                                <Typography.Text>You have no prisoners! You need to mint.</Typography.Text>
                            }
                        </div>
                    </Spin>
                    {/* Mint data */}
                    <Spin spinning={isMintDataLoading}>
                        <div className="layout-content-element">
                            <Typography.Title level={1}>Mint</Typography.Title>
                            {mintData &&
                                <Space direction='vertical'>
                                    <Typography.Paragraph>🔥 {mintData.maxSupplyString - mintData.totalSupplyString} / {mintData.maxSupplyString} unique prisoners left</Typography.Paragraph>
                                    <Typography.Paragraph>💰 {mintData.costString} BNB</Typography.Paragraph>
                                    <Typography.Paragraph>🛒 Max 5 per wallet address</Typography.Paragraph>
                                    <Form
                                        layout='inline'
                                        initialValues={{ amount: "1" }}
                                        onFinish={onFinishMintTokensForm}
                                    >
                                        <Form.Item name="amount">
                                            <Select style={{ width: 120 }} size='large'>
                                                <Select.Option value="1">1 NFT</Select.Option>
                                                <Select.Option value="2">2 NFTs</Select.Option>
                                                <Select.Option value="3">3 NFTs</Select.Option>
                                            </Select>
                                        </Form.Item>

                                        <Form.Item>
                                            <Button type="primary" size='large' htmlType="submit">Mint</Button>
                                        </Form.Item>
                                    </Form>
                                </Space>
                            }
                        </div>
                    </Spin>
                    {/* Leaderboard */}
                    <Spin spinning={isLeaderboardLoading}>
                        <div className="layout-content-element">
                            <Typography.Title level={1}>Leaderboard</Typography.Title>
                            <Alert
                                message="🏆 The top 20 players will receive extra benefits in the next season."
                                type="info"
                            />
                            <div style={{ margin: "24px 0px 36px 0px" }}>
                                <Button onClick={() => { loadLeaderboard() }}>Sync leaderboard</Button>
                            </div>
                            {renderLeaderboard()}
                        </div>
                    </Spin>
                    {/* Voting space */}
                    {IS_VOTING_SPACE_ENABLED && connectedAccontTokens && connectedAccontTokens.length !== 0 &&
                        <Spin spinning={isVotingSpaceLoading}>
                            <div className="layout-content-element">
                                <Typography.Title level={1}>Voting space</Typography.Title>
                                <Alert
                                    message="🔓 This space is only available to owners of prisoners."
                                    type="info"
                                />
                                <Image src={require("../assets/voting_space.gif")} preview={false} width={"320px"} style={{ margin: "24px 0px 24px 0px" }} />
                                <Typography.Paragraph>❓ Right now, you can only own 3 prisoners. Do you want this number to increase to 5?</Typography.Paragraph>
                                <Divider />
                                <Form
                                    layout="vertical"
                                    form={submitDecisionForm}
                                    onFinish={onFinishSubmitDecisionForm}
                                >
                                    <Form.Item
                                        name="decision"
                                        label="Your decision:"
                                        rules={[{ required: true, message: 'Please make a decision!' }]}
                                    >
                                        <Radio.Group>
                                            <Radio value="yes">👍 Yes, I want more prisoners!</Radio>
                                            <Radio value="no">👎 No, we can't change this world!</Radio>
                                        </Radio.Group>
                                    </Form.Item>
                                    <Form.Item>
                                        <Button type="primary" size='large' htmlType="submit">Submit</Button>
                                    </Form.Item>
                                </Form>
                            </div>
                        </Spin>
                    }
                    {/* Creative space */}
                    <Spin spinning={isCreativeSpaceLoading}>
                        <div className="layout-content-element">
                            <Typography.Title level={1}>Creative space</Typography.Title>
                            <Image src={require("../assets/creative_space.gif")} preview={false} width={"240px"} style={{ margin: "0px 0px 12px 0px" }} />
                            <Typography.Paragraph>❓ What do you think prisoners in the crypto space prison can do?</Typography.Paragraph>
                            <Typography.Paragraph>💡 Submit your ideas. Maybe your idea will be implemented as a mini-game or easter egg next season.</Typography.Paragraph>
                            <Divider />
                            <Form
                                layout="vertical"
                                form={submitIdeaForm}
                                onFinish={onFinishSubmitIdeaForm}
                            >
                                <Form.Item name="contact" label="Your Discord or Twitter:" rules={[{ required: true, message: 'Please input your Discord or Twitter!' }]}>
                                    <Input placeholder="@prisoner" />
                                </Form.Item>
                                <Form.Item name="idea" label="Your cool idea:" rules={[{ required: true, message: 'Please input your cool idea!' }]}>
                                    <Input.TextArea placeholder="prisoners can think of an escape plan" />
                                </Form.Item>
                                <Form.Item>
                                    <Button type="primary" size='large' htmlType="submit">Submit</Button>
                                </Form.Item>
                            </Form>
                        </div>
                    </Spin>
                    {/* Community */}
                    <Community />
                </div >
            )
        }
    }

    useEffect(() => {
        analytics.openDashboardPage();
    }, []);

    useEffect(() => {
        getConnectedAccount();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // Init contracts if account connected
        if (connectedAccount && isNetworkChainIdCorrect) {
            const provider = new ethers.providers.Web3Provider(ethereum);
            const signer = provider.getSigner();
            const contractOne = new ethers.Contract(
                ORIGIN_PRISONER_CONTRACT_ADDRESS,
                OriginPrisonerContractJson.abi,
                signer
            );
            const contractTwo = new ethers.Contract(
                ORIGIN_LEADERBOARD_CONTRACT_ADDRESS,
                OriginLeaderboardContractJson.abi,
                signer
            );
            setOriginPrisonerContract(contractOne);
            setOriginLeaderboardContract(contractTwo);
        }
        // Show creative space if account connected
        if (connectedAccount && isNetworkChainIdCorrect) {
            setIsCreativeSpaceLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [connectedAccount, isNetworkChainIdCorrect]);

    useEffect(() => {
        if (originPrisonerContract && originLeaderboardContract) {
            loadConnectedAccountTokens();
            loadMintData();
            loadLeaderboard();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [originPrisonerContract, originLeaderboardContract]);

    return (
        <Layout>
            <Navigation selectedItem="dashboard" />
            <Layout.Content className="layout-content">
                <Spin spinning={isLoading}>
                    {renderContent()}
                </Spin>
            </Layout.Content>
            <Footer />
        </Layout >
    )
}